/**
 * FCM 토큰 관리
 * 
 * 앱 환경 (RN WebView): 네이티브에서 FCM 토큰을 발급받아 Web으로 전달
 * 브라우저 환경: Firebase Web SDK 사용 (현재 미사용)
 * 
 * RN → Web 토큰 전달 방식 2가지:
 *  1. 이벤트 방식: RN이 앱 시작 시 'fcm:token' 이벤트로 푸시
 *  2. 요청 방식: Web이 'GET_FCM_TOKEN' 메시지로 RN에 요청
 */

const FCM_TOKEN_KEY = 'fcmId';
const FCM_TOKEN_TIMEOUT = 5000; // 5초 타임아웃

/**
 * RN 앱 환경 여부 확인
 */
const isAppEnvironment = (): boolean => {
  return !!(window as any).isApp || !!(window as any).ReactNativeWebView;
};

/**
 * RN에 메시지 요청 후 응답을 Promise로 받는 함수
 * (RN App.tsx의 sendToRN 패턴과 동일)
 */
const requestFromRN = (type: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const id = `fcm_req_${Date.now()}`;
    const timeout = setTimeout(() => {
      window.removeEventListener('message', handler);
      reject(new Error('FCM 토큰 요청 타임아웃'));
    }, FCM_TOKEN_TIMEOUT);

    const handler = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        if (data.id === id) {
          clearTimeout(timeout);
          window.removeEventListener('message', handler);
          if (data.error) {
            reject(new Error(data.error));
          } else {
            resolve(data.result || '');
          }
        }
      } catch {
        // 파싱 실패는 무시
      }
    };

    window.addEventListener('message', handler);

    // RN으로 토큰 요청
    (window as any).ReactNativeWebView?.postMessage(
      JSON.stringify({ id, type })
    );
  });
};

/**
 * FCM 토큰 가져오기
 * 
 * 우선순위:
 * 1. localStorage에 저장된 유효 토큰
 * 2. RN 네이티브에 요청 (앱 환경)
 * 3. 빈 문자열 반환 (브라우저 환경 또는 실패 시)
 */
export async function getFCMToken(): Promise<string> {
  // 1. localStorage에 저장된 토큰 확인 (mock 토큰 제외)
  const storedToken = localStorage.getItem(FCM_TOKEN_KEY);
  if (storedToken && !storedToken.startsWith('mock-fcm-token')) {
    console.log('[FCM] 저장된 토큰 사용:', storedToken.substring(0, 20) + '...');
    return storedToken;
  }

  // 2. 앱 환경이면 RN 네이티브에 요청
  if (isAppEnvironment()) {
    try {
      console.log('[FCM] RN 네이티브에 토큰 요청');
      const token = await requestFromRN('GET_FCM_TOKEN');
      if (token) {
        localStorage.setItem(FCM_TOKEN_KEY, token);
        console.log('[FCM] RN에서 토큰 수신 및 저장 완료');
        return token;
      }
    } catch (error) {
      console.warn('[FCM] RN 토큰 요청 실패:', error);
    }
  }

  // 3. 브라우저 환경이거나 실패 시 빈 문자열
  console.warn('[FCM] 토큰을 가져올 수 없습니다. (브라우저 환경 또는 RN 응답 없음)');
  return '';
}

/**
 * RN에서 푸시로 전달된 FCM 토큰을 수신하여 저장
 * App.tsx (RW)에서 최초 1회 호출하여 이벤트 리스너 등록
 * 
 * RN App.tsx의 sendFCMTokenToWeb()이 'fcm:token' 이벤트를 발생시킴
 */
export function initFCMTokenListener(): void {
  // 이미 등록된 경우 중복 등록 방지
  if ((window as any).__fcmListenerRegistered) return;
  (window as any).__fcmListenerRegistered = true;

  // RN이 앱 시작 시 토큰을 'fcm:token' 이벤트로 전달
  window.addEventListener('fcm:token', (event: Event) => {
    const token = (event as CustomEvent).detail?.token;
    if (token && !token.startsWith('mock-fcm-token')) {
      console.log('[FCM] RN 이벤트로 토큰 수신:', token.substring(0, 20) + '...');
      localStorage.setItem(FCM_TOKEN_KEY, token);
    }
  });

  // RN 토큰 갱신 시에도 저장
  window.addEventListener('fcm:token:refresh', (event: Event) => {
    const token = (event as CustomEvent).detail?.token;
    if (token && !token.startsWith('mock-fcm-token')) {
      console.log('[FCM] 토큰 갱신:', token.substring(0, 20) + '...');
      localStorage.setItem(FCM_TOKEN_KEY, token);
    }
  });

  console.log('[FCM] 토큰 리스너 등록 완료');
}

/**
 * 저장된 FCM 토큰 삭제 (로그아웃 시 호출)
 */
export function clearFCMToken(): void {
  localStorage.removeItem(FCM_TOKEN_KEY);
  console.log('[FCM] 토큰 삭제 완료');
}